import * as React from "react";
import Link from "@mui/material/Link";
import Typography from "@mui/material/Typography";
import {Divider, Grid2, Stack, useTheme} from "@mui/material";
import {useTranslation} from "react-i18next";
import {AcademyBanner, JHHoldingBanner, LinkedInBanner} from "@/agentur/AgenturIcons";
import {ButtonEx} from "@/lib/components/buttons/ButtonEx";
import {color_green} from "@/agentur/AgenturColors";
import Box from "@mui/material/Box";
import {AppPaths} from "@/app/AppPaths";
import {loadContactData} from "@/pages/landing-page/landing-page-data";
import {CommonI18N} from "@/translations";
import {responsiveDirection, responsiveGridSize, useScreenClass} from "@/lib/screen-class";

interface FooterProps {
    landingPage?: boolean;
}

const BRAND_PLACEHOLDER = "{brand}";

/** Size of every footer block: one column on narrow screens, two on medium ones. */
const FOOTER_BLOCK_SIZE = responsiveGridSize({ md: 3, lg: 2 });

/**
 * Renders the initiative paragraph while preserving the shared "BoxHub" brand highlight (bold "Box").
 * The `{brand}` placeholder in the localized copy marks where the brand name is rendered inline.
 */
const InitiativeText = ({ initiative }: { initiative: string }) => {
    const parts = initiative.split(BRAND_PLACEHOLDER);
    return (
        <>
            {parts.map((part, index) => (
                <React.Fragment key={`${index}:${part}`}>
                    {index > 0 && (
                        <>
                            <strong>Box</strong>Hub
                        </>
                    )}
                    {part}
                </React.Fragment>
            ))}
        </>
    );
};

const AddressBlock = () => {
    const { t } = useTranslation();
    return (
        <Grid2 size={FOOTER_BLOCK_SIZE} container direction="column">
            <Typography variant="body2" margin={0} fontWeight={600}>
                {t(CommonI18N.footer.contact)}
            </Typography>
            <Typography variant="body2" margin={0}>
                JH Holding GmbH
            </Typography>
            <Typography variant="body2" margin={0}>
                Maximilianstraße 43
            </Typography>
            <Grid2>
                <Typography variant="body2">67433 Neustadt an der Weinstraße</Typography>
            </Grid2>
        </Grid2>
    );
};

const ContactPersonBlock = () => {
    const { t } = useTranslation();
    const contactData = loadContactData();
    return (
        <Grid2 size={FOOTER_BLOCK_SIZE} direction="column">
            <Typography variant="body2" fontWeight={600}>
                {t(CommonI18N.footer.contactPerson)}
            </Typography>
            <Typography variant="body2">{contactData.name}</Typography>
            <Typography variant="body2">
                <Link href={`mailto:${contactData.email}`}>{contactData.email}</Link>
            </Typography>
            <Typography variant="body2">
                <Link href={`tel:${contactData.phone}`}>{contactData.phone}</Link>
            </Typography>
        </Grid2>
    );
};

const LegalBlock = () => {
    const { t } = useTranslation();
    return (
        <Grid2 size={FOOTER_BLOCK_SIZE} container direction="column">
            <Typography variant="body2" fontWeight={600}>
                {t(CommonI18N.footer.legal)}
            </Typography>
            <Typography variant="body2">
                <Link href={`#${AppPaths.IMPRESSUM_PAGE}`}>{t(CommonI18N.footer.imprint)}</Link>
            </Typography>
            <Typography variant="body2">
                <Link href={`#${AppPaths.PRIVACY_PAGE}`}>{t(CommonI18N.footer.privacy)}</Link>
            </Typography>
            <Typography variant="body2">
                <Link href={`#${AppPaths.COOKIE_POLICY_PAGE}`}>{t(CommonI18N.footer.cookieSettings)}</Link>
            </Typography>
        </Grid2>
    );
};

/** Width of the member area button and the two banners when they stack, the banners' own width. */
const STACKED_LINK_WIDTH = 180;

/** Offsets the `margin: -3px` that `DynamicSvgIcon` puts on every banner. */
const BANNER_OVERHANG = "3px";

const MEMBER_AREA_SX ={ height: 52, borderRadius: 3, backgroundColor: color_green[2] };

const JhHoldingLink = () => (
    <Link href="https://jhh.de/">
        <JHHoldingBanner />
    </Link>
);

const AcademyLink = () => (
    <Link href="https://www.professionals-academy.de/">
        <AcademyBanner />
    </Link>
);

const LinkedInLink = () => (
    <Link href="https://de.linkedin.com/company/jh-holding-gmbh">
        <LinkedInBanner />
    </Link>
);

/**
 * Narrow screens: the member area button and the two banners stack centered at one width, LinkedIn follows in its
 * own row below a divider. The banners sit in flex boxes so no line box adds height below them; centering them there
 * cancels the symmetric negative margin `DynamicSvgIcon` sets, which would shift them 3 px left of the button. The
 * same margin lets each banner overhang its box by 3 px above and below; the vertical padding keeps every gap in the
 * column equal.
 */
const NarrowFooterLinks = () => {
    const { t } = useTranslation();
    return (
        <Stack alignItems="center" spacing={2} padding={2} paddingTop={4}>
            <Stack spacing={2} width={STACKED_LINK_WIDTH}>
                <Box data-cy="footer-member-area">
                    {/* The default side padding lets the German label overflow the 180 px button; ButtonEx forces
                        nowrap, so a longer label (Italian) wraps inside its own span instead of being clipped */}
                    <ButtonEx
                        sx={{ ...MEMBER_AREA_SX, px: 1 }}
                        fullWidth
                        label={
                            <Box component="span" sx={{ whiteSpace: "normal", lineHeight: 1.2 }}>
                                {t(CommonI18N.footer.memberArea)}
                            </Box>
                        }
                        to="/sign-in"
                    />
                </Box>
                <Box data-cy="footer-banner-jhholding" display="flex" justifyContent="center" py={BANNER_OVERHANG}>
                    <JhHoldingLink />
                </Box>
                <Box data-cy="footer-banner-academy" display="flex" justifyContent="center" py={BANNER_OVERHANG}>
                    <AcademyLink />
                </Box>
            </Stack>
            <Divider sx={{ width: STACKED_LINK_WIDTH }} />
            <Box data-cy="footer-linkedin" display="flex">
                <LinkedInLink />
            </Box>
        </Stack>
    );
};

/** The bottom row of the landing page footer: one row from `sm` upwards, a centered column on narrow screens. */
const FooterLinks = () => {
    const { t } = useTranslation();
    if (useScreenClass() === "narrow") {
        return <NarrowFooterLinks />;
    }
    return (
        <Grid2 container width="100%" padding={2} direction="row" spacing={2} justifyContent="center" paddingTop={4}>
            <Grid2 data-cy="footer-member-area">
                <ButtonEx
                    sx={{ ...MEMBER_AREA_SX, marginTop: "-3px" }}
                    label={t(CommonI18N.footer.memberArea)}
                    to="/sign-in"
                />
            </Grid2>
            <Grid2 data-cy="footer-banner-jhholding">
                <JhHoldingLink />
            </Grid2>
            <Grid2 data-cy="footer-banner-academy">
                <AcademyLink />
            </Grid2>
            <Grid2 data-cy="footer-linkedin">
                <LinkedInLink />
            </Grid2>
        </Grid2>
    );
};

export const Footer = ({ landingPage }: FooterProps) => {
    const theme = useTheme();
    const { t } = useTranslation();

    return (
        <Box paddingTop={2} sx={{ width: "100%" }}>
            {/*<Grid2 container direction="column" padding={2} justifyContent="center">*/}
            {theme.palette.mode === "dark" ? <Divider /> : null}
            <Grid2
                width="100%"
                justifyContent="space-evenly"
                container
                padding={2}
                // flexWrap="nowrap"
                direction={responsiveDirection()}
                gap={4}
                sx={{
                    backgroundColor: theme.palette.background.default,
                    borderTopLeftRadius: "8px",
                    borderTopRightRadius: "8px"
                }}
            >
                {/* Grid2 subtracts columnSpacing, not the gap, from item widths: below md it repeats the gap */}
                <Grid2
                    container
                    direction="row"
                    gap={2}
                    columnSpacing={{ xs: 2, md: 0 }}
                    flexGrow={1}
                    justifyContent="space-evenly"
                >
                    {landingPage && (
                        <Grid2 size={FOOTER_BLOCK_SIZE}>
                            <Typography variant="body2">
                                <InitiativeText initiative={t(CommonI18N.footer.initiative)} />
                            </Typography>
                        </Grid2>
                    )}
                    <AddressBlock />
                    <ContactPersonBlock />
                    <LegalBlock />
                </Grid2>
            </Grid2>
            {landingPage && <FooterLinks />}
            {/*</Grid2>*/}
        </Box>
    );
};
